import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Stack,
  Group,
  Text,
  Title,
  Badge,
  Button,
  Alert,
  ThemeIcon,
  Collapse,
} from '@mantine/core';
import {
  IconCircleCheck,
  IconAlertTriangle,
  IconArrowRight,
  IconRefresh,
} from '@tabler/icons-react';
import type { ImportWizardState, BulkImportResponse } from '../types';
import { CURRENCIES } from '@/types/livestock';

interface CompleteStepProps {
  state: ImportWizardState;
  importResult: BulkImportResponse | null;
  onReset: () => void;
}

export function CompleteStep({ state, importResult, onReset }: CompleteStepProps) {
  const navigate = useNavigate();
  const [errorsOpen, setErrorsOpen] = useState(false);

  if (!importResult) {
    return (
      <Stack gap="lg" mt="lg">
        <Text c="dimmed" ta="center" py="xl">
          No import results available.
        </Text>
      </Stack>
    );
  }

  const currencyInfo = CURRENCIES.find((c) => c.code === state.currency);
  const currencySymbol = currencyInfo?.symbol ?? '$';

  const formattedValue =
    importResult.summary.total_value != null
      ? `${currencySymbol}${importResult.summary.total_value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : null;

  const livestockLabel =
    state.livestockType.charAt(0).toUpperCase() + state.livestockType.slice(1);
  const pluralLabel = importResult.imported !== 1 ? `${livestockLabel}s` : livestockLabel;

  const hasErrors = importResult.errors.length > 0;

  return (
    <Stack gap="lg" mt="lg">
      <Paper withBorder p="xl">
        <Stack align="center" gap="md">
          <ThemeIcon size={64} radius="xl" color="green" variant="light">
            <IconCircleCheck size={36} />
          </ThemeIcon>

          <Title order={2}>Import Complete</Title>

          <Text size="xl" fw={700}>
            {importResult.imported} {pluralLabel.toLowerCase()} imported
          </Text>

          <Group gap="lg" justify="center">
            <Group gap={6}>
              <Badge variant="light" color="green" size="lg">
                Alive: {importResult.summary.alive}
              </Badge>
            </Group>
            <Group gap={6}>
              <Badge variant="light" color="red" size="lg">
                Deceased: {importResult.summary.deceased}
              </Badge>
            </Group>
          </Group>

          {formattedValue && (
            <Text size="sm" c="dimmed">
              Total value: {formattedValue}
            </Text>
          )}

          {importResult.skipped_duplicates > 0 && (
            <Text size="sm" c="dimmed">
              Duplicates skipped: {importResult.skipped_duplicates}
            </Text>
          )}

          {importResult.failed > 0 && (
            <Badge variant="light" color="red" size="md">
              {importResult.failed} failed
            </Badge>
          )}

          <Group gap="md" mt="md">
            <Button
              onClick={() => navigate('/livestock')}
              rightSection={<IconArrowRight size={16} />}
            >
              View Livestock
            </Button>
            <Button
              variant="default"
              onClick={onReset}
              leftSection={<IconRefresh size={16} />}
            >
              Import More
            </Button>
          </Group>
        </Stack>
      </Paper>

      {hasErrors && (
        <Alert
          color="yellow"
          icon={<IconAlertTriangle size={20} />}
          title={`${importResult.errors.length} row${importResult.errors.length !== 1 ? 's' : ''} had errors`}
        >
          <Stack gap="xs">
            <Text size="sm">
              Some rows could not be imported. Click below to see details.
            </Text>
            <Button
              variant="subtle"
              size="xs"
              onClick={() => setErrorsOpen((o) => !o)}
            >
              {errorsOpen ? 'Hide errors' : 'Show errors'}
            </Button>
            <Collapse in={errorsOpen}>
              <Stack gap={4} mt="xs">
                {importResult.errors.map((err, idx) => (
                  <Text key={idx} size="xs" c="dimmed">
                    Row {err.index + 1}: {err.message}
                  </Text>
                ))}
              </Stack>
            </Collapse>
          </Stack>
        </Alert>
      )}
    </Stack>
  );
}
